import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Search,
  RefreshCw,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Receipt,
  Timer,
  DollarSign,
  Activity,
  Zap,
  Filter,
  Calendar,
  Wallet,
  Package,
  Shield,
  Camera,
  Video,
  ClipboardList,
  Building,
  MapPin
} from "lucide-react";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';
const REFRESH_INTERVAL = 10000; // 10 seconds auto-refresh
const SEARCH_DEBOUNCE_MS = 400; // Debounce search input

export default function AdminLiveSessions() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state for debounce
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [dateFilter, setDateFilter] = useState(searchParams.get("date") || "");
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Sync filter state with URL searchParams on in-component navigation
  useEffect(() => {
    const urlStatus = searchParams.get("status") || "all";
    const urlDate = searchParams.get("date") || "";
    if (urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
      setCurrentPage(0);
    }
    if (urlDate !== dateFilter) {
      setDateFilter(urlDate);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedSession, setSelectedSession] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const refreshRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [user, setUser] = useState(null);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        navigate("/admin/login");
      }
    };
    checkAuth();
  }, [navigate]);

  // Debounced search - wait for user to stop typing
  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(0); // Reset to first page on new search
    }, SEARCH_DEBOUNCE_MS);
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      // For Live Sessions screen:
      // - "all" = live view showing today's non-whitelisted Active sessions
      // - other statuses = historical view with that specific terminal status
      if (statusFilter === "all") {
        // Live view: all current non-whitelisted sessions (display as Active)
        params.append("view", "live");
      } else {
        // Historical view with specific terminal status
        params.append("status", statusFilter);
        params.append("view", "all");
      }
      
      if (dateFilter) params.append("date", dateFilter);
      if (searchTerm.trim()) params.append("vrm", searchTerm.trim());
      params.append("limit", pageSize.toString());
      params.append("skip", (currentPage * pageSize).toString());

      const [sessionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/parking/sessions?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/parking/stats`, { withCredentials: true })
      ]);

      setSessions(sessionsRes.data?.sessions || []);
      setTotalCount(sessionsRes.data?.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired, please login again");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter, searchTerm, currentPage, pageSize, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshRef.current = setInterval(fetchData, REFRESH_INTERVAL);
    } else {
      if (refreshRef.current) clearInterval(refreshRef.current);
    }
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [autoRefresh, fetchData]);

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  // Format helpers
  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (entryTime, exitTime = null) => {
    if (!entryTime) return "—";
    const entry = new Date(entryTime);
    const end = exitTime ? new Date(exitTime) : new Date();
    const diffMs = end - entry;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 0) return "—";
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "£0.00";
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    // For Live Non-Whitelisted Sessions view:
    // active, pending_payment, and paid (with no exit) all display as "Active"
    // These are internal lifecycle states, not operator-facing status labels
    const liveStatuses = ["active", "pending_payment", "paid"];
    
    if (liveStatuses.includes(status)) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
          <Activity className="w-3 h-3" />
          Active
        </span>
      );
    }
    
    // Terminal/historical statuses keep their distinct labels
    const badges = {
      completed: { bg: "bg-blue-500/20", text: "text-blue-400", icon: CheckCircle, label: "Completed" },
      closed: { bg: "bg-slate-500/20", text: "text-slate-400", icon: CheckCircle, label: "Closed" },
      cancelled: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle, label: "Cancelled" },
      expired: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: Clock, label: "Expired" },
      whitelisted: { bg: "bg-purple-500/20", text: "text-purple-400", icon: Shield, label: "Whitelisted" }
    };
    
    const badge = badges[status] || { bg: "bg-gray-500/20", text: "text-gray-400", icon: Clock, label: status || "Unknown" };
    const Icon = badge.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (session) => {
    if (session.is_whitelisted === true) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
          <Shield className="w-3 h-3" />
          Whitelisted
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
        <DollarSign className="w-3 h-3" />
        Payable
      </span>
    );
  };

  const getPaymentBadge = (session) => {
    if (session.payment_status === "paid" || session.outstanding === 0) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
          <CheckCircle className="w-3 h-3" />
          Paid
        </span>
      );
    }
    if (session.outstanding > 0) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
          <DollarSign className="w-3 h-3" />
          {formatCurrency(session.outstanding)}
        </span>
      );
    }
    return (
      <span className="text-xs text-slate-500">—</span>
    );
  };

  // Sessions are now filtered by backend - no client-side filtering needed
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-live-sessions">
      {/* Executive Dashboard Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-1">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm" data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" />
            <span>Dashboard</span>
          </Link>
          
          {/* Operations */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Operations</p>
            <Link to="/admin/freeflow" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Shield className="w-4 h-4" />
              <span>Freeflow Whitelist</span>
            </Link>
            <Link to="/admin/exit-auths" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Clock className="w-4 h-4" />
              <span>Exit Authorisations</span>
            </Link>
            <Link to="/admin/barrier" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Activity className="w-4 h-4" />
              <span>Barrier Control</span>
            </Link>
            <Link to="/admin/cameras" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Camera className="w-4 h-4" />
              <span>Cameras</span>
            </Link>
            <Link to="/admin/cctv" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm" data-testid="nav-cctv">
              <Video className="w-4 h-4" />
              <span>CCTV</span>
            </Link>
            <Link to="/admin/daily-movement" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <ClipboardList className="w-4 h-4" />
              <span>Daily VRM Movement</span>
            </Link>
            <Link to="/admin/vehicle-history" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Search className="w-4 h-4" />
              <span>Vehicle History</span>
            </Link>
          </div>
          
          {/* Commercial */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <DollarSign className="w-4 h-4" />
              <span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Package className="w-4 h-4" />
              <span>Products & Tariffs</span>
            </Link>
            <Link to="/admin/commercial/sites" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Building className="w-4 h-4" />
              <span>Sites</span>
            </Link>
          </div>

          {/* Admin */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Admin</p>
            <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <FileText className="w-4 h-4" />
              <span>Registrations</span>
            </Link>
            <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Building className="w-4 h-4" />
              <span>Sites & Kiosks</span>
            </Link>
            <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>OCR Exceptions</span>
            </Link>
          </div>

          {/* Engagement */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Engagement</p>
            <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Store className="w-4 h-4" />
              <span>Retailers</span>
            </Link>
            <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Gift className="w-4 h-4" />
              <span>Rewards</span>
            </Link>
            <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </Link>
          </div>

          {/* Signage */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Monitor className="w-4 h-4" />
              <span>Screens</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <ListChecks className="w-4 h-4" />
              <span>Playlists</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <FileText className="w-4 h-4" />
              <span>Content</span>
            </Link>
          </div>

          {/* Revenue */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Megaphone className="w-4 h-4" />
              <span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>Campaigns</span>
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <CreditCard className="w-4 h-4" />
              <span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <BarChart3 className="w-4 h-4" />
              <span>Revenue</span>
            </Link>
          </div>

          {/* System */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">System</p>
            <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full" data-testid="logout-button">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Activity className="w-8 h-8 text-green-400" />
                Live Parking Sessions
              </h1>
              <p className="text-slate-400 mt-1">
                Real-time view of all parking sessions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={autoRefresh ? "bg-green-600 hover:bg-green-700" : "border-slate-700 text-slate-300"}
                data-testid="auto-refresh-toggle"
              >
                <Zap className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
                {autoRefresh ? 'Live' : 'Paused'}
              </Button>
              <Button 
                onClick={fetchData} 
                variant="outline" 
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card 
                className="bg-slate-900 border-slate-800 cursor-pointer hover:border-green-500/50 transition-colors"
                onClick={() => navigate("/admin/sessions?status=active")}
                data-testid="stats-card-active"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white" data-testid="live-sessions-count">
                        {stats.live_sessions ?? 0}
                      </p>
                      <p className="text-xs text-slate-400">Non-Whitelisted Sessions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-slate-900 border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors"
                onClick={() => navigate("/admin/sessions?status=completed&date=today")}
                data-testid="stats-card-completed"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.completed_today || 0}</p>
                      <p className="text-xs text-slate-400">Completed Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-slate-900 border-slate-800 cursor-pointer hover:border-yellow-500/50 transition-colors"
                onClick={() => navigate("/admin/commercial/finance?preset=today")}
                data-testid="stats-card-revenue"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{formatCurrency(stats.revenue_today || 0)}</p>
                      <p className="text-xs text-slate-400">Revenue Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-slate-900 border-slate-800 cursor-pointer hover:border-purple-500/50 transition-colors"
                onClick={() => navigate("/admin/commercial/finance?preset=today")}
                data-testid="stats-card-avg-spend"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">
                        £{((stats.avg_spend_today_pence || 0) / 100).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400">Average Spend Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search by VRM (e.g., GY16 or GY16XZK)..."
                      value={searchInput}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      data-testid="search-input"
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                  {searchTerm && (
                    <p className="text-xs text-slate-500 mt-1">
                      Searching for &quot;{searchTerm}&quot; - {totalCount} result{totalCount !== 1 ? 's' : ''} found
                    </p>
                  )}
                </div>

                <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setDateFilter(""); setCurrentPage(0); }}>
                  <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700 text-white" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">Active ({stats?.live_sessions ?? 0})</SelectItem>
                    <SelectItem value="completed" className="text-white">Completed</SelectItem>
                    <SelectItem value="closed" className="text-white">Closed</SelectItem>
                    <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date filter indicator and clear */}
                {dateFilter && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-1 rounded">
                      Today only
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDateFilter(""); setStatusFilter("all"); navigate("/admin/sessions"); }}
                      className="text-slate-400 hover:text-white h-7 px-2"
                      data-testid="clear-filters-btn"
                    >
                      Clear filters
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sessions Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <Car className="w-5 h-5" />
                {statusFilter === "all" ? "Live Sessions" : "Parking Sessions"} ({totalCount})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-12">
                  <Car className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">{searchTerm ? `No sessions found for "${searchTerm}"` : "No sessions found"}</p>
                  {searchTerm && (
                    <button 
                      onClick={() => { setSearchInput(""); setSearchTerm(""); }}
                      className="mt-2 text-sm text-blue-400 hover:text-blue-300"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Vehicle</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Entry</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Duration</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Payment</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Site</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {sessions.map((session) => (
                        <tr 
                          key={session.session_id} 
                          className="hover:bg-slate-800/30 transition-colors"
                          data-testid={`session-row-${session.session_id}`}
                        >
                          <td className="px-4 py-3">
                            <span className="font-mono font-bold text-white">
                              {session.vehicle_registration}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm">
                            {formatDateTime(session.entry_time)}
                          </td>
                          <td className="px-4 py-3 text-slate-300">
                            {formatDuration(session.entry_time, session.exit_time)}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(session.display_status || session.status)}
                          </td>
                          <td className="px-4 py-3">
                            {getTypeBadge(session)}
                          </td>
                          <td className="px-4 py-3">
                            {getPaymentBadge(session)}
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-sm">
                            {session.site_id}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-white"
                              onClick={() => setSelectedSession(session)}
                              data-testid={`view-session-${session.session_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
                  <p className="text-sm text-slate-400">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="bg-slate-800 border-slate-700 text-white disabled:opacity-50"
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-3 text-sm text-slate-400">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="bg-slate-800 border-slate-700 text-white disabled:opacity-50"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Session Detail Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Car className="w-5 h-5" />
              Session Details
            </DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 bg-slate-800/50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-slate-500">Session ID</p>
                  <p className="text-white font-mono text-sm">{selectedSession.session_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Vehicle</p>
                  <p className="text-white font-mono font-bold">{selectedSession.vehicle_registration}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Entry Time</p>
                  <p className="text-white">{formatDateTime(selectedSession.entry_time)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Exit Time</p>
                  <p className="text-white">{formatDateTime(selectedSession.exit_time) || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  {getStatusBadge(selectedSession.display_status || selectedSession.status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Duration</p>
                  <p className="text-white">
                    {formatDuration(selectedSession.entry_time, selectedSession.exit_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Payment Status</p>
                  {getPaymentBadge(selectedSession)}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Site</p>
                  <p className="text-white">{selectedSession.site_id || '—'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
